import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from "react";
import { cx } from "@jugl-web/utils";
import {
  DelayedRender,
  InteractiveContainer,
  LoadingSpinner,
} from "@jugl-web/ui-components";
import { useMediaPlayer } from "@web-src/modules/media/providers/MediaPlayerProvider/useMediaPlayer";
import { useUnmount } from "react-use";
import { ReactComponent as PlayIcon } from "./assets/play.svg";
import { ReactComponent as PauseIcon } from "./assets/pause.svg";

const getDurationStorageKey = (src: string) => `audio:duration:${src}`;

interface VoiceMessageAudioPlayerProps {
  audioSrc: string;
  waveform: number[];
  variant: "incoming" | "outgoing";
  duration?: number;
}

const roundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
  ctx.fill();
};

export const VoiceMessageAudioPlayer: React.FC<
  VoiceMessageAudioPlayerProps
> = ({ audioSrc, waveform, variant, duration: defaultDuration = 0 }) => {
  const {
    currentAudio,
    playAudio,
    pauseAudio,
    resumeAudio,
    setCurrentTime,
    stopAudio,
  } = useMediaPlayer();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const [storedDuration, setStoredDuration] = useState(() => {
    if (defaultDuration) {
      return defaultDuration;
    }
    const storedValue = sessionStorage.getItem(getDurationStorageKey(audioSrc));
    if (storedValue && !Number.isNaN(Number(storedValue))) {
      return Number(storedValue);
    }
    return 0;
  });
  const [isDragging, setIsDragging] = useState(false);
  const isOutgoing = variant === "outgoing";

  const isThisAudioLoaded = currentAudio?.src === audioSrc;
  const isThisAudioPlaying = isThisAudioLoaded && currentAudio.isPlaying;
  const playingDuration = isThisAudioLoaded ? currentAudio.currentTime : 0;
  const audioDuration = isThisAudioLoaded
    ? currentAudio.duration
    : defaultDuration || storedDuration;

  // Store duration in session storage when it becomes available
  useEffect(() => {
    if (isThisAudioLoaded && currentAudio.duration > 0 && !defaultDuration) {
      sessionStorage.setItem(
        getDurationStorageKey(audioSrc),
        currentAudio.duration.toString()
      );
      setStoredDuration(currentAudio.duration);
    }
  }, [isThisAudioLoaded, currentAudio?.duration, audioSrc, defaultDuration]);

  const drawWaveform = useCallback(
    (actualTime: number) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (ctx && canvas) {
        const dpr = window.devicePixelRatio || 1;
        const canvasWidth = canvas.offsetWidth;
        const canvasHeight = canvas.offsetHeight;

        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;

        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = false;

        const segmentWidth = canvasWidth / waveform.length;
        const progressX = (actualTime / audioDuration) * canvasWidth;

        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        waveform.forEach((value, index) => {
          const barHeight = Math.max(3, value * canvasHeight * 0.8);
          const x = index * segmentWidth;
          const barWidth = segmentWidth * 0.3;
          const barX = x + (segmentWidth - barWidth) / 2;
          const barY = canvasHeight - barHeight;
          const progressInBar = Math.max(
            0,
            Math.min(barWidth, progressX - barX)
          );

          ctx.fillStyle = isOutgoing ? "#FFFFFF" : "#A4A5AA";
          roundedRect(ctx, barX, barY, barWidth, barHeight, barWidth / 2);
          if (progressInBar > 0) {
            ctx.fillStyle = isOutgoing ? "#90C9F9" : "#1A75D2";
            roundedRect(
              ctx,
              barX,
              barY,
              progressInBar,
              barHeight,
              barWidth / 2
            );
          }
        });
      }
    },
    [waveform, isOutgoing, audioDuration]
  );

  // Update waveform when currentTime changes
  useEffect(() => {
    if (!isDragging && isThisAudioLoaded) {
      drawWaveform(playingDuration);
    }
  }, [playingDuration, isThisAudioLoaded, isDragging, drawWaveform]);

  // Initial drawing
  useEffect(() => {
    drawWaveform(isThisAudioLoaded ? playingDuration : 0);
  }, [drawWaveform, isThisAudioLoaded, playingDuration]);

  const handleCanvasClick = useCallback(
    (e: MouseEvent | React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (canvas && isThisAudioLoaded) {
        const rect = canvas.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickTime = (clickX / rect.width) * audioDuration;
        setCurrentTime(clickTime);
        drawWaveform(clickTime);
      }
    },
    [drawWaveform, audioDuration, setCurrentTime, isThisAudioLoaded]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent | React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
      if (isThisAudioLoaded) {
        setIsDragging(true);
        handleCanvasClick(e);
      }
    },
    [handleCanvasClick, isThisAudioLoaded]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging && isThisAudioLoaded) {
        handleCanvasClick(e);
      }
    },
    [handleCanvasClick, isDragging, isThisAudioLoaded]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isThisAudioLoaded) return undefined;

    canvas.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [handleMouseMove, handleMouseDown, handleMouseUp, isThisAudioLoaded]);

  const togglePlayPause = useCallback(() => {
    if (isThisAudioPlaying) {
      pauseAudio();
    } else if (isThisAudioLoaded) {
      resumeAudio();
    } else {
      if (currentAudio) {
        stopAudio();
      }
      playAudio(audioSrc, "Voice Message");
    }
  }, [
    isThisAudioPlaying,
    isThisAudioLoaded,
    pauseAudio,
    resumeAudio,
    currentAudio,
    stopAudio,
    playAudio,
    audioSrc,
  ]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
      .toString()
      .padStart(2, "0");
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const buttonClassNames = useMemo(() => {
    if (isOutgoing) {
      if (isThisAudioPlaying) {
        return "bg-white text-gradients-g3 hover:bg-grey-200";
      }
      return "bg-white text-primary hover:bg-grey-200";
    }
    if (isThisAudioPlaying) {
      return "bg-gradients-g3 text-white hover:bg-[#9D4AD7]";
    }
    return "bg-primary text text-white hover:bg-primary-600";
  }, [isThisAudioPlaying, isOutgoing]);

  // Cleanup on unmount
  useUnmount(() => {
    if (currentAudio?.src === audioSrc) {
      stopAudio();
    }
  });

  return (
    <div className="flex items-center gap-2.5 p-1">
      <InteractiveContainer
        onClick={togglePlayPause}
        className={cx(
          "bg-primary flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors",
          buttonClassNames
        )}
      >
        <DelayedRender
          isLoading={isThisAudioPlaying && !!currentAudio?.isLoading}
          loadingContent={<LoadingSpinner className="h-5 w-5" />}
        >
          {isThisAudioPlaying ? <PauseIcon /> : <PlayIcon />}
        </DelayedRender>
      </InteractiveContainer>
      <div className="flex w-full flex-col gap-1">
        <canvas
          ref={canvasRef}
          className={cx("h-10 w-full", isThisAudioLoaded && "cursor-pointer")}
        />
        <span
          className={cx("text-sm", isOutgoing ? "text-white" : "text-grey")}
          ref={timeRef}
        >
          {isThisAudioLoaded
            ? formatTime(playingDuration)
            : formatTime(audioDuration)}
        </span>
      </div>
    </div>
  );
};
